import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
    Button,
    CircularProgress,
    Modal,
    Paper,
    Slide,
    Typography,
    ButtonBase,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@material-ui/core'
import { DateTime } from 'luxon'

import { useScheduleModalStyles } from './styles'
import {
    SchedulePhase,
    ScheduleMethod,
    scheduleMethodToCreationSource,
} from './types'
import { ScheduleMethodSelection } from './ScheduleMethodSelection'
import { StepLori } from './StepLori'
import { Review } from './Review'
import { SchedulerAssignModal } from '../SchedulerAssignModal/SchedulerAssignModal'
import { Folder } from '../../../../models/Infrastructure'
import CloseIcon from '@material-ui/icons/Close'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import {
    Area,
    AreaConfig,
    AreaStatusConfig,
    AreaStatusTag,
    BaseWorkorder,
    ApartmentVendor,
    Company,
    IdBoolMap,
    Lease,
    ListVendor,
    ModelListMap,
    ModelMap,
    Schedule,
    Service,
    UnitConfig,
} from '../../../../models'
import {
    CreateWorkOrderRequest,
    CreateWorkorderActionThunk,
} from '../../../../store'
import { FinderSelection } from '../../../../hooks'
import { VersionedTree } from '../../../../hooks/useInfrastructure/types'
import { useLoriState } from '../../../../contexts/LoriContext'
import { useEzSchedule } from '../../../../hooks/useEzSchedule/useEzSchedule'
import { AutoSchedule } from './AutoSchedule'
import { useTimezone } from '../../../../contexts/timezone'
import { AdvancedLori } from './AdvancedLori'
import { DuplicateTurnWizard } from './DuplicateTurnWizard'
import { axiosInstance } from '../../../../helpers'
import { WalkScheduler, WalkView } from './WalkScheduler'
import {
    WalkSchedulerFrame,
    WalkSchedulerStepper,
} from './WalkSchedulerStepper'

interface Props {
    open: boolean
    onClose: () => void
    initialMethod?: ScheduleMethod | null
    initialPhase?: SchedulePhase
    scheduleId: number
    infrastructure: Folder
    serviceList: Service[]
    vendorList: ListVendor[]
    schedule?: Schedule
    apartment: Company
    filteredTree?: VersionedTree
    unitConfigMap?: ModelMap<UnitConfig>
    areaConfigMap?: ModelMap<AreaConfig>
    unitWorkorderMap?: ModelListMap<BaseWorkorder>
    areaLeaseMap?: ModelListMap<Lease>
    serviceAreaMap?: IdBoolMap
    assignmentFinderSelection?: FinderSelection
    areaStatusTagMap?: ModelMap<AreaStatusTag>
    areaStatusConfigList?: AreaStatusConfig[]
    createWorkorder?: (
        req: CreateWorkOrderRequest,
    ) => CreateWorkorderActionThunk
    isAreaInFilter?: (area: Area) => boolean
    reloadScheduleWorkorders: () => void
    workordersLoading: boolean
    apartmentVendorList: ApartmentVendor[]
    onAfterSchedule?: () => void
}

export const ScheduleModal = (props: Props) => {
    const {
        open,
        onClose,
        initialMethod,
        initialPhase,
        scheduleId,
        infrastructure,
        serviceList,
        vendorList,
        schedule: scheduleProp,
        filteredTree,
        unitConfigMap,
        areaConfigMap,
        unitWorkorderMap,
        areaLeaseMap,
        serviceAreaMap,
        assignmentFinderSelection,
        areaStatusTagMap,
        areaStatusConfigList,
        createWorkorder,
        isAreaInFilter,
        apartment,
        reloadScheduleWorkorders,
        workordersLoading,
        apartmentVendorList,
        onAfterSchedule,
    } = props
    const assignUnitMaps = {
        unitConfigMap: unitConfigMap ?? {},
        areaConfigMap: areaConfigMap ?? {},
        areaStatusTagMap: areaStatusTagMap ?? {},
    }
    const classes = useScheduleModalStyles()
    const scheduleModalClasses = classes as Record<string, string>
    const theme = useTheme()
    const {
        buttonEvent,
        freshMessageIds,
        removeFromFreshMessageIds,
    } = useLoriState()

    // build turn project button deprecation?
    const [buildTurnProjectButton, setBuildTurnProjectButton] = useState(false)

    const [loriReviewButton, setLoriReviewButton] = useState(false)
    const [
        retryPreviousMessageButton,
        setRetryPreviousMessageButton,
    ] = useState(false)
    const [getPreviousTurnsButton, setGetPreviousTurnsButton] = useState(false)
    const [selectedMethod, setSelectedMethod] = useState<ScheduleMethod | null>(
        null,
    )
    const [walkCommitted, setWalkCommitted] = useState<boolean | null>(null)
    const [walkView, setWalkView] = useState<WalkView | null>(null)
    const [walkBack, setWalkBack] = useState<(() => void) | null>(null)
    const [duplicateWizardComplete, setDuplicateWizardComplete] = useState(
        false,
    )
    const [duplicateWarnings, setDuplicateWarnings] = useState<string[]>([])
    const templateCreationSource = scheduleMethodToCreationSource(
        selectedMethod,
    )
    const [phaseId, setPhaseId] = useState<number | undefined>(undefined)
    const [phaseHelpDialogOpen, setPhaseHelpDialogOpen] = useState<boolean>(
        false,
    )
    const [removePhaseConfirmOpen, setRemovePhaseConfirmOpen] = useState(false)

    const [schedulePhase, setSchedulePhase] = useState<SchedulePhase>('setup')
    const {
        ezSchedule,
        ezScheduleLoading,
        ezScheduleFetching,
        ezScheduleError,
        refetchEzSchedule,
        updateEzSchedule,
        updateEzScheduleAsync,
        templateIsValid,
        templateWorkorders,
        generateWorkorders,
        addTemplateService,
        deleteTemplateService,
        addTemplateServiceVendor,
        updateTemplateServiceVendor,
        deleteTemplateServiceVendor,
        setTemplateUnits,
        updateTemplateService,
        generateTemplateWorkorders,
        templateWorkordersLoading,
        generateTemplateWorkordersLoading,
        refetchTemplateWorkorders,
        refetchUnitCoverage,
        unitCoverage,
        templateWorkordersByPhase,
        unitCoverageByPhase,
        templateSchedules,
        templateSchedulesLoading,
        templateSchedulesError,
        addPhase,
        removePhase,
        resetPhase,
        duplicatableSchedules,
        targetHasSetup,
        duplicatableSchedulesLoading,
        duplicateFromSchedule,
        duplicateFromScheduleLoading,
    } = useEzSchedule({
        scheduleId,
        enabled: open,
        includeTemplateSchedules:
            selectedMethod === 'lori_advanced' && getPreviousTurnsButton,
        phaseId: phaseId,
    })

    const { currentTimezone } = useTimezone()

    const handleWalkViewChange = useCallback((view: WalkView) => {
        setWalkView(view)
    }, [])
    const handleWalkBackChange = useCallback((handler: (() => void) | null) => {
        setWalkBack(() => handler)
    }, [])

    useEffect(() => {
        if (open) {
            setSelectedMethod(initialMethod ?? null)
            setBuildTurnProjectButton(false)
            setLoriReviewButton(false)
            setSchedulePhase(initialPhase ?? 'setup')
            setRetryPreviousMessageButton(false)
            setGetPreviousTurnsButton(false)
            setDuplicateWizardComplete(false)
            setDuplicateWarnings([])
            setWalkView(null)
            setWalkBack(null)
        }
    }, [open, initialMethod, initialPhase])

    useEffect(() => {
        if (!open) {
            setWalkCommitted(null)
            setWalkView(null)
            return
        }
        const inspectionId = scheduleProp?.inspection
        if (!inspectionId) {
            setWalkCommitted(false)
            return
        }
        let cancelled = false
        setWalkCommitted(null)
        axiosInstance
            .get(`inspection/${inspectionId}/`)
            .then((res) => {
                if (cancelled) return
                const committed = Boolean(res.data?.walk_scheduler)
                setWalkCommitted(committed)
                if (committed) {
                    setSelectedMethod('walk_scheduler')
                    setSchedulePhase('setup')
                }
            })
            .catch(() => {
                if (!cancelled) setWalkCommitted(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, scheduleProp?.inspection, initialMethod])

    useEffect(() => {
        if (!open || walkCommitted !== true) return
        setSelectedMethod('walk_scheduler')
    }, [open, walkCommitted])

    useEffect(() => {
        const phases = ezSchedule?.schedule_phases ?? []
        if (phases.length === 0) return

        const currentPhaseExists = phases.some((phase) => phase.id === phaseId)
        if (!currentPhaseExists) {
            setPhaseId(phases[0].id)
        }
    }, [ezSchedule?.schedule_phases, phaseId])

    const handlePhaseChange = (nextPhaseId: number) => {
        setPhaseId(nextPhaseId)
    }

    const handleAddPhase = () => {
        void addPhase()
    }

    const handleRemovePhase = () => {
        if (phaseId === undefined) return
        const remainingPhases = (ezSchedule?.schedule_phases ?? []).filter(
            (phase) => phase.id !== phaseId,
        )
        void removePhase({ phaseId }).then(() => {
            if (remainingPhases.length > 0) {
                setPhaseId(remainingPhases[0].id)
            }
        })
        setRemovePhaseConfirmOpen(false)
    }

    const selectedPhaseIndex = useMemo(() => {
        if (!ezSchedule || phaseId === undefined) return undefined
        return ezSchedule.schedule_phases.find((p) => p.id === phaseId)
            ?.phase_index
    }, [ezSchedule, phaseId])

    const selectedPhase = useMemo(() => {
        if (!ezSchedule) return null
        return (
            ezSchedule.phases.find((phase) => phase.phase?.id === phaseId) ??
            null
        )
    }, [ezSchedule, phaseId])

    const reviewEzSchedule = useMemo(() => {
        if (!ezSchedule || !selectedPhase) return ezSchedule

        return {
            ...ezSchedule,
            template_units: selectedPhase.template_units,
            template_services: selectedPhase.template_services,
            template_vendors: selectedPhase.template_vendors,
            work_start_date:
                selectedPhase.work_start_date === undefined
                    ? ezSchedule.work_start_date
                    : selectedPhase.work_start_date,
            work_end_date:
                selectedPhase.work_end_date === undefined
                    ? ezSchedule.work_end_date
                    : selectedPhase.work_end_date,
        }
    }, [ezSchedule, selectedPhase])

    const reviewTemplateIsValid =
        selectedPhase?.template_is_valid ?? templateIsValid
    const isMultiPhaseEnabled = Boolean(ezSchedule?.use_multi_phase)

    useEffect(() => {
        if (!buttonEvent || !freshMessageIds.has(buttonEvent.messageId)) {
            return
        }

        if (buttonEvent.type === 'build_turn_project') {
            setBuildTurnProjectButton(true)
            removeFromFreshMessageIds(buttonEvent.messageId)
        } else if (buttonEvent.type === 'lori_review') {
            removeFromFreshMessageIds(buttonEvent.messageId)
            setLoriReviewButton(true)
            void refetchEzSchedule()
        } else if (buttonEvent.type === 'retry_previous_message') {
            setRetryPreviousMessageButton(true)
            removeFromFreshMessageIds(buttonEvent.messageId)
        } else if (buttonEvent.type === 'get_previous_turns') {
            setGetPreviousTurnsButton(true)
            removeFromFreshMessageIds(buttonEvent.messageId)
        }
    }, [buttonEvent, freshMessageIds, removeFromFreshMessageIds])

    useEffect(() => {
        if (!open || schedulePhase !== 'review') {
            return
        }

        void refetchTemplateWorkorders()
        void refetchUnitCoverage()
    }, [open, schedulePhase, refetchTemplateWorkorders, refetchUnitCoverage])

    useEffect(() => {
        if (!isMultiPhaseEnabled && phaseHelpDialogOpen) {
            setPhaseHelpDialogOpen(false)
        }
    }, [isMultiPhaseEnabled, phaseHelpDialogOpen])

    const renderAutoSchedulerContent = () => {
        switch (schedulePhase) {
            case 'setup':
                return apartment ? (
                    <>
                        {selectedMethod === 'duplicate_turn' &&
                            duplicateWarnings.length > 0 && (
                                <div
                                    style={{
                                        margin: 16,
                                        padding: '12px 16px',
                                        borderRadius: 10,
                                        background: 'rgba(245, 124, 0, 0.08)',
                                        border: '1px solid #ffb74d',
                                        color: '#e65100',
                                        fontSize: 13,
                                    }}
                                >
                                    {duplicateWarnings.join(' ')}
                                </div>
                            )}
                        <AutoSchedule
                            ezSchedule={ezSchedule}
                            phaseId={phaseId}
                            ezScheduleLoading={ezScheduleLoading}
                            infrastructure={infrastructure}
                            vendorList={vendorList}
                            serviceList={serviceList}
                            onPhaseChange={setSchedulePhase}
                            updateEzSchedule={updateEzSchedule}
                            addTemplateService={addTemplateService}
                            deleteTemplateService={deleteTemplateService}
                            addTemplateServiceVendor={addTemplateServiceVendor}
                            deleteTemplateServiceVendor={
                                deleteTemplateServiceVendor
                            }
                            updateTemplateServiceVendor={
                                updateTemplateServiceVendor
                            }
                            setTemplateUnits={setTemplateUnits}
                            updateTemplateService={updateTemplateService}
                            generateTemplateWorkorders={
                                generateTemplateWorkorders
                            }
                            refetchTemplateWorkorders={
                                refetchTemplateWorkorders
                            }
                        />
                    </>
                ) : (
                    <ScheduleMethodSelection
                        setSelectedMethod={setSelectedMethod}
                        hasInspection={Boolean(scheduleProp?.inspection)}
                        ezSchedule={ezSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                        canDuplicateTurn={duplicatableSchedules.length > 0}
                        duplicatableSchedulesLoading={
                            duplicatableSchedulesLoading
                        }
                    />
                )
            case 'review':
                if (!reviewEzSchedule) {
                    return null
                }
                return (
                    <Review
                        theme={theme}
                        ezSchedule={reviewEzSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                        ezScheduleFetching={ezScheduleFetching}
                        templateWorkorders={templateWorkorders}
                        unitCoverage={unitCoverage}
                        selectedPhaseId={phaseId}
                        schedulePhases={ezSchedule?.schedule_phases ?? []}
                        templateWorkordersByPhase={templateWorkordersByPhase}
                        unitCoverageByPhase={unitCoverageByPhase}
                        templateIsValid={reviewTemplateIsValid}
                        serviceList={serviceList}
                        vendorList={vendorList}
                        infrastructure={infrastructure}
                        {...assignUnitMaps}
                        updateEzSchedule={updateEzSchedule}
                        generateWorkorders={generateWorkorders}
                        updateTemplateServiceVendor={
                            updateTemplateServiceVendor
                        }
                        generateTemplateWorkorders={generateTemplateWorkorders}
                        templateWorkordersLoading={templateWorkordersLoading}
                        generateTemplateWorkordersLoading={
                            generateTemplateWorkordersLoading
                        }
                        onCloseModal={onClose}
                        reloadScheduleWorkorders={reloadScheduleWorkorders}
                        workordersLoading={workordersLoading}
                        templateCreationSource={templateCreationSource}
                        onAfterSchedule={onAfterSchedule}
                    />
                )
            default:
                return apartment ? (
                    <AutoSchedule
                        ezSchedule={ezSchedule}
                        phaseId={phaseId}
                        ezScheduleLoading={ezScheduleLoading}
                        infrastructure={infrastructure}
                        vendorList={vendorList}
                        serviceList={serviceList}
                        onPhaseChange={setSchedulePhase}
                        updateEzSchedule={updateEzSchedule}
                        addTemplateService={addTemplateService}
                        deleteTemplateService={deleteTemplateService}
                        addTemplateServiceVendor={addTemplateServiceVendor}
                        deleteTemplateServiceVendor={
                            deleteTemplateServiceVendor
                        }
                        updateTemplateServiceVendor={
                            updateTemplateServiceVendor
                        }
                        setTemplateUnits={setTemplateUnits}
                        updateTemplateService={updateTemplateService}
                        generateTemplateWorkorders={generateTemplateWorkorders}
                        refetchTemplateWorkorders={refetchTemplateWorkorders}
                    />
                ) : (
                    <ScheduleMethodSelection
                        setSelectedMethod={setSelectedMethod}
                        hasInspection={Boolean(scheduleProp?.inspection)}
                        ezSchedule={ezSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                        canDuplicateTurn={duplicatableSchedules.length > 0}
                        duplicatableSchedulesLoading={
                            duplicatableSchedulesLoading
                        }
                    />
                )
        }
    }

    const renderContent = () => {
        if (
            scheduleProp?.inspection &&
            (walkCommitted === null ||
                (walkCommitted && selectedMethod !== 'walk_scheduler'))
        ) {
            return (
                <div className={classes.walkFlagLoading}>
                    <CircularProgress />
                </div>
            )
        }
        switch (selectedMethod) {
            // Lori Step
            case 'lori_step':
                switch (schedulePhase) {
                    case 'setup':
                        return (
                            <StepLori
                                infrastructure={infrastructure}
                                onPhaseChange={setSchedulePhase}
                                phaseId={phaseId}
                                ezSchedule={ezSchedule}
                                buildTurnProjectButton={buildTurnProjectButton}
                                loriReviewButton={loriReviewButton}
                                retryPreviousMessageButton={
                                    retryPreviousMessageButton
                                }
                                setTemplateUnits={setTemplateUnits}
                                ezScheduleFetching={ezScheduleFetching}
                                onGoBack={() => setSelectedMethod(null)}
                                resetPhase={() =>
                                    resetPhase({ phaseId: phaseId ?? -1 })
                                }
                            />
                        )
                    case 'review':
                        if (!reviewEzSchedule) {
                            return null
                        }
                        return (
                            <Review
                                theme={theme}
                                ezSchedule={reviewEzSchedule}
                                ezScheduleLoading={ezScheduleLoading}
                                ezScheduleFetching={ezScheduleFetching}
                                templateWorkorders={templateWorkorders}
                                unitCoverage={unitCoverage}
                                selectedPhaseId={phaseId}
                                schedulePhases={
                                    ezSchedule?.schedule_phases ?? []
                                }
                                templateWorkordersByPhase={
                                    templateWorkordersByPhase
                                }
                                unitCoverageByPhase={unitCoverageByPhase}
                                templateIsValid={reviewTemplateIsValid}
                                serviceList={serviceList}
                                vendorList={vendorList}
                                infrastructure={infrastructure}
                                {...assignUnitMaps}
                                updateEzSchedule={updateEzSchedule}
                                generateWorkorders={generateWorkorders}
                                updateTemplateServiceVendor={
                                    updateTemplateServiceVendor
                                }
                                generateTemplateWorkorders={
                                    generateTemplateWorkorders
                                }
                                templateWorkordersLoading={
                                    templateWorkordersLoading
                                }
                                generateTemplateWorkordersLoading={
                                    generateTemplateWorkordersLoading
                                }
                                onCloseModal={onClose}
                                reloadScheduleWorkorders={
                                    reloadScheduleWorkorders
                                }
                                workordersLoading={workordersLoading}
                                templateCreationSource={templateCreationSource}
                                onAfterSchedule={onAfterSchedule}
                            />
                        )
                    default:
                        return (
                            <StepLori
                                infrastructure={infrastructure}
                                phaseId={phaseId}
                                ezSchedule={ezSchedule}
                                onPhaseChange={setSchedulePhase}
                                buildTurnProjectButton={buildTurnProjectButton}
                                loriReviewButton={loriReviewButton}
                                retryPreviousMessageButton={
                                    retryPreviousMessageButton
                                }
                                setTemplateUnits={setTemplateUnits}
                                ezScheduleFetching={ezScheduleFetching}
                                onGoBack={() => setSelectedMethod(null)}
                                resetPhase={() =>
                                    resetPhase({ phaseId: phaseId ?? -1 })
                                }
                            />
                        )
                }
            // Lori Advanced
            case 'lori_advanced':
                switch (schedulePhase) {
                    case 'setup':
                        return (
                            <AdvancedLori
                                infrastructure={infrastructure}
                                onPhaseChange={setSchedulePhase}
                                ezSchedule={ezSchedule}
                                buildTurnProjectButton={buildTurnProjectButton}
                                loriReviewButton={loriReviewButton}
                                retryPreviousMessageButton={
                                    retryPreviousMessageButton
                                }
                                setTemplateUnits={setTemplateUnits}
                                getPreviousTurnsButton={getPreviousTurnsButton}
                                apartmentVendorList={apartmentVendorList}
                                templateSchedules={templateSchedules}
                                templateSchedulesLoading={
                                    templateSchedulesLoading
                                }
                                templateSchedulesError={templateSchedulesError}
                                phaseId={phaseId ?? -1}
                                ezScheduleFetching={ezScheduleFetching}
                                onGoBack={() => setSelectedMethod(null)}
                                resetPhase={() =>
                                    resetPhase({ phaseId: phaseId ?? -1 })
                                }
                            />
                        )
                    case 'review':
                        if (!reviewEzSchedule) {
                            return null
                        }
                        return (
                            <Review
                                theme={theme}
                                ezSchedule={reviewEzSchedule}
                                ezScheduleLoading={ezScheduleLoading}
                                ezScheduleFetching={ezScheduleFetching}
                                templateWorkorders={templateWorkorders}
                                unitCoverage={unitCoverage}
                                selectedPhaseId={phaseId}
                                schedulePhases={
                                    ezSchedule?.schedule_phases ?? []
                                }
                                templateWorkordersByPhase={
                                    templateWorkordersByPhase
                                }
                                unitCoverageByPhase={unitCoverageByPhase}
                                templateIsValid={reviewTemplateIsValid}
                                serviceList={serviceList}
                                vendorList={vendorList}
                                infrastructure={infrastructure}
                                {...assignUnitMaps}
                                updateEzSchedule={updateEzSchedule}
                                generateWorkorders={generateWorkorders}
                                updateTemplateServiceVendor={
                                    updateTemplateServiceVendor
                                }
                                generateTemplateWorkorders={
                                    generateTemplateWorkorders
                                }
                                templateWorkordersLoading={
                                    templateWorkordersLoading
                                }
                                generateTemplateWorkordersLoading={
                                    generateTemplateWorkordersLoading
                                }
                                onCloseModal={onClose}
                                reloadScheduleWorkorders={
                                    reloadScheduleWorkorders
                                }
                                workordersLoading={workordersLoading}
                                templateCreationSource={templateCreationSource}
                                onAfterSchedule={onAfterSchedule}
                            />
                        )
                    default:
                        return (
                            <AdvancedLori
                                infrastructure={infrastructure}
                                onPhaseChange={setSchedulePhase}
                                ezSchedule={ezSchedule}
                                buildTurnProjectButton={buildTurnProjectButton}
                                loriReviewButton={loriReviewButton}
                                retryPreviousMessageButton={
                                    retryPreviousMessageButton
                                }
                                setTemplateUnits={setTemplateUnits}
                                getPreviousTurnsButton={getPreviousTurnsButton}
                                apartmentVendorList={apartmentVendorList}
                                templateSchedules={templateSchedules}
                                templateSchedulesLoading={
                                    templateSchedulesLoading
                                }
                                templateSchedulesError={templateSchedulesError}
                                phaseId={phaseId ?? -1}
                                ezScheduleFetching={ezScheduleFetching}
                                onGoBack={() => setSelectedMethod(null)}
                                resetPhase={() =>
                                    resetPhase({ phaseId: phaseId ?? -1 })
                                }
                            />
                        )
                }
            // Duplicate previous turn (wizard, then Auto Scheduler setup/review)
            case 'duplicate_turn':
                if (!duplicateWizardComplete) {
                    if (!ezSchedule) {
                        return null
                    }
                    return (
                        <DuplicateTurnWizard
                            targetSchedule={ezSchedule}
                            duplicatableSchedules={duplicatableSchedules}
                            targetHasSetup={targetHasSetup}
                            loading={duplicatableSchedulesLoading}
                            vendorList={vendorList}
                            submitting={duplicateFromScheduleLoading}
                            onGoBack={() => setSelectedMethod(null)}
                            onSubmit={async (params) => {
                                const response = await duplicateFromSchedule(
                                    params,
                                )
                                return response.data
                            }}
                            onComplete={(result) => {
                                // Cache is already seeded in duplicateFromSchedule
                                // onSuccess (before this runs) so Auto Scheduler
                                // mounts with included units available.
                                setDuplicateWarnings(result.warnings ?? [])
                                setDuplicateWizardComplete(true)
                                setSchedulePhase('setup')
                                const firstPhaseId =
                                    result.schedule.schedule_phases?.[0]?.id
                                if (firstPhaseId != null) {
                                    setPhaseId(firstPhaseId)
                                }
                            }}
                        />
                    )
                }
                return renderAutoSchedulerContent()
            case 'walk_scheduler':
                if (schedulePhase === 'review') {
                    return (
                        <WalkSchedulerFrame>
                            {renderAutoSchedulerContent()}
                        </WalkSchedulerFrame>
                    )
                }
                if (!scheduleProp?.inspection) {
                    return (
                        <ScheduleMethodSelection
                            setSelectedMethod={setSelectedMethod}
                            hasInspection={false}
                            ezSchedule={ezSchedule}
                            ezScheduleLoading={ezScheduleLoading}
                        />
                    )
                }
                return (
                    <WalkScheduler
                        inspectionId={scheduleProp.inspection}
                        onExit={() => {
                            setSelectedMethod(null)
                            setSchedulePhase('setup')
                            setWalkView(null)
                        }}
                        walkView={walkView}
                        onWalkViewChange={handleWalkViewChange}
                        onWalkBackChange={handleWalkBackChange}
                        ezSchedule={ezSchedule}
                        phaseId={phaseId}
                        ezScheduleLoading={ezScheduleLoading}
                        infrastructure={infrastructure}
                        schedulerInfrastructure={filteredTree?.root}
                        {...assignUnitMaps}
                        vendorList={vendorList}
                        serviceList={serviceList}
                        onPhaseChange={setSchedulePhase}
                        updateEzSchedule={updateEzSchedule}
                        updateEzScheduleAsync={updateEzScheduleAsync}
                        addTemplateService={addTemplateService}
                        deleteTemplateService={deleteTemplateService}
                        addTemplateServiceVendor={addTemplateServiceVendor}
                        deleteTemplateServiceVendor={
                            deleteTemplateServiceVendor
                        }
                        updateTemplateServiceVendor={
                            updateTemplateServiceVendor
                        }
                        setTemplateUnits={setTemplateUnits}
                        updateTemplateService={updateTemplateService}
                        generateTemplateWorkorders={generateTemplateWorkorders}
                        generateTemplateWorkordersLoading={
                            generateTemplateWorkordersLoading
                        }
                        templateWorkordersLoading={templateWorkordersLoading}
                        unitCoverage={unitCoverage}
                        templateWorkordersByPhase={templateWorkordersByPhase}
                        unitCoverageByPhase={unitCoverageByPhase}
                        generateWorkorders={generateWorkorders}
                        refetchTemplateWorkorders={refetchTemplateWorkorders}
                        templateWorkorders={templateWorkorders}
                        reloadScheduleWorkorders={reloadScheduleWorkorders}
                        workordersLoading={workordersLoading}
                        onCloseModal={onClose}
                        onAfterSchedule={onAfterSchedule}
                    />
                )
            // Auto Scheduler
            case 'auto_scheduler':
                return renderAutoSchedulerContent()
            // Bulk Assign
            case 'bulk_assign':
                if (
                    scheduleProp &&
                    filteredTree &&
                    unitConfigMap &&
                    areaConfigMap &&
                    unitWorkorderMap &&
                    serviceAreaMap &&
                    assignmentFinderSelection &&
                    areaStatusTagMap &&
                    areaStatusConfigList &&
                    createWorkorder &&
                    isAreaInFilter
                ) {
                    return (
                        <SchedulerAssignModal
                            embedded
                            onClose={onClose}
                            filteredTree={filteredTree}
                            unitConfigMap={unitConfigMap}
                            areaConfigMap={areaConfigMap}
                            unitWorkorderMap={unitWorkorderMap}
                            areaLeaseMap={areaLeaseMap ?? {}}
                            schedule={scheduleProp}
                            serviceList={serviceList}
                            vendorList={vendorList}
                            serviceAreaMap={serviceAreaMap}
                            assignmentFinderSelection={
                                assignmentFinderSelection
                            }
                            areaStatusTagMap={areaStatusTagMap}
                            areaStatusConfigList={areaStatusConfigList}
                            createWorkorder={createWorkorder}
                            isAreaInFilter={isAreaInFilter}
                        />
                    )
                }
                return (
                    <ScheduleMethodSelection
                        setSelectedMethod={setSelectedMethod}
                        hasInspection={Boolean(scheduleProp?.inspection)}
                        ezSchedule={ezSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                    />
                )
            case null:
            default:
                return (
                    <ScheduleMethodSelection
                        setSelectedMethod={setSelectedMethod}
                        hasInspection={Boolean(scheduleProp?.inspection)}
                        ezSchedule={ezSchedule}
                        ezScheduleLoading={ezScheduleLoading}
                        canDuplicateTurn={duplicatableSchedules.length > 0}
                        duplicatableSchedulesLoading={
                            duplicatableSchedulesLoading
                        }
                    />
                )
        }
    }

    const showWalkStepper = selectedMethod === 'walk_scheduler'
    const walkStepIndex =
        schedulePhase === 'review'
            ? 2
            : walkView === 'progress'
            ? 1
            : walkView === 'schedule'
            ? 2
            : walkView === 'setup'
            ? 0
            : -1
    const scheduleInfo = ezSchedule ? (
        <div
            className={
                showWalkStepper
                    ? `${classes.scheduleInfoCard} ${classes.scheduleInfoCardCompact}`
                    : classes.scheduleInfoCard
            }
        >
            <div className={classes.scheduleInfoText}>
                <Typography className={classes.scheduleInfoName}>
                    {ezSchedule.name}
                </Typography>
                <Typography className={classes.scheduleInfoDates}>
                    {DateTime.fromISO(ezSchedule.start_date)
                        .setZone(currentTimezone)
                        .toFormat('MMM d, yyyy')}{' '}
                    -{' '}
                    {DateTime.fromISO(ezSchedule.end_date)
                        .setZone(currentTimezone)
                        .toFormat('MMM d, yyyy')}
                </Typography>
            </div>
            {isMultiPhaseEnabled && (
                <div className={classes.phasePillRow}>
                    {ezSchedule.schedule_phases.map((phase) => (
                        <ButtonBase
                            key={phase.id}
                            className={`${classes.phasePill} ${
                                phaseId === phase.id
                                    ? classes.phasePillActive
                                    : ''
                            }`}
                            onClick={() => handlePhaseChange(phase.id)}
                        >
                            {`Phase ${phase.phase_index}`}
                        </ButtonBase>
                    ))}
                    <ButtonBase
                        className={classes.phaseAddPill}
                        onClick={handleAddPhase}
                    >
                        + Add Phase
                    </ButtonBase>
                    <ButtonBase
                        className={scheduleModalClasses.phaseRemovePill}
                        onClick={() => setRemovePhaseConfirmOpen(true)}
                        disabled={
                            (ezSchedule?.schedule_phases?.length ?? 0) <= 1
                        }
                    >
                        - Remove Phase
                    </ButtonBase>
                    <Button
                        className={scheduleModalClasses.phaseHelpButton}
                        onClick={() => setPhaseHelpDialogOpen(true)}
                        startIcon={<InfoOutlinedIcon fontSize="small" />}
                        aria-label="What are phases?"
                    >
                        What&apos;s this?
                    </Button>
                </div>
            )}
        </div>
    ) : null

    return (
        <Modal open={open} onClose={onClose}>
            <Slide direction="up" in={open}>
                <Paper className={classes.root}>
                    {/* Header */}
                    <div
                        className={
                            showWalkStepper
                                ? classes.headerWithStepper
                                : classes.header
                        }
                    >
                        <div className={classes.headerLeft}>
                            {selectedMethod && (
                                <ButtonBase
                                    className={classes.backControl}
                                    onClick={() => {
                                        if (
                                            (selectedMethod === 'lori_step' ||
                                                selectedMethod ===
                                                    'lori_advanced' ||
                                                selectedMethod ===
                                                    'auto_scheduler' ||
                                                selectedMethod ===
                                                    'duplicate_turn' ||
                                                selectedMethod ===
                                                    'walk_scheduler') &&
                                            schedulePhase === 'review'
                                        ) {
                                            setSchedulePhase('setup')
                                            setBuildTurnProjectButton(false)
                                            return
                                        }
                                        if (
                                            scheduleProp?.inspection &&
                                            walkCommitted !== false
                                        ) {
                                            walkBack?.()
                                            return
                                        }
                                        if (
                                            selectedMethod ===
                                                'walk_scheduler' &&
                                            walkBack
                                        ) {
                                            walkBack()
                                            return
                                        }
                                        // After a successful duplicate, Go back
                                        // returns to method selection (not the wizard).
                                        setSelectedMethod(null)
                                        setBuildTurnProjectButton(false)
                                        setDuplicateWizardComplete(false)
                                        setDuplicateWarnings([])
                                    }}
                                >
                                    <ArrowBackIcon fontSize="small" />
                                    <Typography
                                        variant="body2"
                                        className={classes.backLabel}
                                    >
                                        {(selectedMethod === 'lori_step' ||
                                            selectedMethod ===
                                                'lori_advanced' ||
                                            selectedMethod ===
                                                'auto_scheduler' ||
                                            selectedMethod ===
                                                'duplicate_turn' ||
                                            selectedMethod ===
                                                'walk_scheduler') &&
                                        schedulePhase === 'review'
                                            ? selectedMethod ===
                                              'walk_scheduler'
                                                ? 'Back to scheduling'
                                                : 'Back to setup'
                                            : 'Go back'}
                                    </Typography>
                                </ButtonBase>
                            )}
                            {showWalkStepper && (
                                <WalkSchedulerStepper
                                    activeStep={walkStepIndex}
                                />
                            )}
                        </div>
                        <div className={classes.headerTitle}>
                            {scheduleInfo}
                        </div>
                        <IconButton
                            onClick={onClose}
                            className={classes.closeButton}
                            size="small"
                            aria-label="Close schedule modal"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    {/* Body */}
                    <div className={classes.body}>{renderContent()}</div>
                    <Dialog
                        open={isMultiPhaseEnabled && phaseHelpDialogOpen}
                        onClose={() => setPhaseHelpDialogOpen(false)}
                        fullWidth
                        maxWidth="sm"
                        aria-labelledby="phase-help-dialog-title"
                    >
                        <DialogTitle id="phase-help-dialog-title">
                            <Typography
                                variant="h6"
                                className={scheduleModalClasses.phaseHelpTitle}
                            >
                                Phases in a Project
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Typography
                                className={scheduleModalClasses.phaseHelpIntro}
                            >
                                You can see Phases because you selected a
                                multi-phase turn in settings.
                            </Typography>
                            <Typography
                                className={scheduleModalClasses.phaseHelpIntro}
                            >
                                Phases let you schedule multiple turns within a
                                single project.
                            </Typography>
                            <Typography
                                className={scheduleModalClasses.phaseHelpIntro}
                            >
                                If you do not require this, no action is
                                required and you can use the scheduler as you
                                always have.
                            </Typography>
                            <Typography
                                className={scheduleModalClasses.phaseHelpIntro}
                            >
                                If you would like to use the multi-phase turn:
                            </Typography>
                            <ul className={scheduleModalClasses.phaseHelpList}>
                                <li
                                    className={
                                        scheduleModalClasses.phaseHelpListItem
                                    }
                                >
                                    Select <strong>+ Add Phase</strong>.
                                </li>
                                <li
                                    className={
                                        scheduleModalClasses.phaseHelpListItem
                                    }
                                >
                                    {' '}
                                    Either schedule each phase separately, or
                                    you can schedule all phases at once.
                                </li>
                            </ul>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setPhaseHelpDialogOpen(false)}
                                color="primary"
                                variant="contained"
                            >
                                Got it
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={removePhaseConfirmOpen}
                        onClose={() => setRemovePhaseConfirmOpen(false)}
                        fullWidth
                        maxWidth="xs"
                        aria-labelledby="remove-phase-dialog-title"
                    >
                        <DialogTitle id="remove-phase-dialog-title">
                            Remove Phase
                        </DialogTitle>
                        <DialogContent>
                            <Typography>
                                Are you sure you want to remove Phase{' '}
                                {selectedPhaseIndex}?
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setRemovePhaseConfirmOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleRemovePhase}
                                color="secondary"
                                variant="contained"
                            >
                                Remove
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Slide>
        </Modal>
    )
}
